import VideoIntro from "./VideoIntro";
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  heroData,
  aboutSectionData,
  whyChooseUsData,
  platformLogos,
} from "../data/mockData";
import {
  ArrowRight,
  BarChart3,
  Target,
  TrendingUp,
  Zap,
  ShoppingCart,
  Globe,
  Monitor,
  Radio,
  Award,
} from "lucide-react";

const iconMap = {
  BarChart3: BarChart3,
  Target: Target,
  TrendingUp: TrendingUp,
};

/* ─── Global Keyframes (injected once) ─── */
const GlobalStyles = () => (
  <style>{`
    @keyframes blink {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0; }
    }
    @keyframes shimmer {
      0%   { transform: translateX(-100%) skewX(-15deg); }
      100% { transform: translateX(250%) skewX(-15deg); }
    }
    @keyframes pulse-ring {
      0%   { transform: scale(1);    opacity: 0.6; }
      100% { transform: scale(1.55); opacity: 0;   }
    }
    @keyframes float-arrow {
      0%, 100% { transform: translateX(0);  }
      50%       { transform: translateX(5px); }
    }
    @keyframes btn-pulse-scale {
      0%, 100% { transform: scale(1); }
      50%      { transform: scale(1.02); }
    }
    @keyframes btn-entrance {
      0% {
        opacity: 0;
        transform: translateY(20px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes ring-pulse {
      0% {
        box-shadow: 0 0 0 0 rgba(242,101,34,0.7);
      }
      70% {
        box-shadow: 0 0 0 10px rgba(242,101,34,0);
      }
      100% {
        box-shadow: 0 0 0 0 rgba(242,101,34,0);
      }
    }
    @keyframes marquee-scroll {
      0%   { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    .marquee-track {
      display: flex;
      gap: 32px;
      width: max-content;
      animation: marquee-scroll 32s linear infinite;
    }
    .marquee-track:hover {
      animation-play-state: paused;
    }
  `}</style>
);

/* ─── Rotating Headline with Typewriter Animation ─── */
const RotatingHeadline = ({ headlines }) => {
  const [current, setCurrent] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [phase, setPhase] = useState("typing");
  const timeoutRef = useRef(null);

  const LOGO_BLUE     = "#2020FF";
  const TYPE_SPEED    = 30;
  const ERASE_SPEED   = 15;
  const WAIT_DURATION = 2200;

  useEffect(() => {
    if (!headlines || headlines.length === 0) return;
    const currentText = headlines[current];

    if (phase === "typing") {
      if (displayed.length < currentText.length) {
        timeoutRef.current = setTimeout(() => {
          setDisplayed(currentText.slice(0, displayed.length + 1));
        }, TYPE_SPEED);
      } else {
        timeoutRef.current = setTimeout(() => setPhase("erasing"), WAIT_DURATION);
      }
    }

    if (phase === "erasing") {
      if (displayed.length > 0) {
        timeoutRef.current = setTimeout(() => {
          setDisplayed(displayed.slice(0, -1));
        }, ERASE_SPEED);
      } else {
        setCurrent((prev) => (prev + 1) % headlines.length);
        setPhase("typing");
      }
    }

    return () => clearTimeout(timeoutRef.current);
  }, [displayed, phase, current, headlines]);

  if (!headlines || headlines.length === 0) return null;

  return (
    <span
      style={{
        display: "inline-block",
        color: LOGO_BLUE,
        minHeight: "1.2em",
        fontFamily: "'Poppins', sans-serif",
        position: "relative",
      }}
    >
      {displayed}
      <span
        style={{
          display: "inline-block",
          width: "3px",
          height: "0.85em",
          background: LOGO_BLUE,
          marginLeft: "3px",
          verticalAlign: "middle",
          borderRadius: "1px",
          animation: "blink 1s step-end infinite",
        }}
      />
    </span>
  );
};

/* ─── Animated Audit CTA Button ─── */
const AuditButton = ({ to, children }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to={to}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="no-underline"
      style={{ position: "relative", display: "inline-flex" }}
    >
      {/* Outer pulse ring */}
      <span
        style={{
          position: "absolute",
          inset: "-8px",
          borderRadius: "9999px",
          animation: "ring-pulse 2s ease-out infinite",
          pointerEvents: "none",
        }}
      />

      {/* Pulse rings on hover */}
      <span
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "9999px",
          border: `2px solid rgba(242,101,34,${hovered ? 0.8 : 0.5})`,
          animation: "pulse-ring 1.6s ease-out infinite",
          pointerEvents: "none",
          opacity: hovered ? 1 : 0.6,
          transition: "opacity 0.3s ease",
        }}
      />
      <span
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "9999px",
          border: `2px solid rgba(242,101,34,${hovered ? 0.5 : 0.3})`,
          animation: "pulse-ring 1.6s ease-out infinite 0.55s",
          pointerEvents: "none",
          opacity: hovered ? 0.8 : 0.4,
          transition: "opacity 0.3s ease",
        }}
      />

      {/* Main pill */}
      <span
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          gap: "10px",
          background: hovered
            ? "linear-gradient(135deg, #ff8844 0%, #F26522 50%, #e0501a 100%)"
            : "linear-gradient(135deg, #F26522 0%, #e0501a 100%)",
          color: "#fff",
          padding: "16px 32px",
          borderRadius: "9999px",
          fontSize: "14px",
          fontWeight: "700",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          fontFamily: "'Poppins', sans-serif",
          overflow: "hidden",
          transition: "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.3s ease, box-shadow 0.3s ease",
          transform: hovered ? "translateY(-3px) scale(1.05)" : "translateY(0) scale(1)",
          animation: hovered ? "btn-entrance 0.6s ease-out" : "btn-pulse-scale 3s ease-in-out infinite",
          cursor: "pointer",
          whiteSpace: "nowrap",
          boxShadow: hovered
            ? "0 0 40px 8px rgba(242,101,34,0.6), 0 8px 40px rgba(242,101,34,0.4)"
            : "0 0 25px 4px rgba(242,101,34,0.5), 0 4px 24px rgba(242,101,34,0.25)",
        }}
      >
        {/* Shimmer sweep on hover */}
        {hovered && (
          <span
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "40%",
              height: "100%",
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
              animation: "shimmer 0.7s ease forwards",
              pointerEvents: "none",
            }}
          />
        )}

        {children}

        <ArrowRight
          size={16}
          style={{
            flexShrink: 0,
            animation: hovered ? "float-arrow 0.8s ease-in-out infinite" : "none",
            transition: "all 0.3s ease",
          }}
        />
      </span>
    </Link>
  );
};

/* ─── Platforms Grid ─── */
const PlatformsGrid = () => {
  const logos = platformLogos || [];
  return (
    <div className="grid grid-cols-4 md:grid-cols-8 gap-4 md:gap-6 items-center justify-items-center">
      {logos.map((platform, index) => (
        <div key={index} className="flex flex-col items-center gap-2 group">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white shadow-sm flex items-center justify-center p-3 group-hover:shadow-md transition-all duration-300">
            <img
              src={platform.image}
              alt={platform.name}
              className="w-full h-full object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
            />
          </div>
          <span
            className="text-[10px] md:text-[11px] text-[#888] text-center leading-tight"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {platform.name}
          </span>
        </div>
      ))}
    </div>
  );
};

/* ─── Client Logos ─── */
const clientLogos = [
  { name: "Amazin Homes",         image: "/clients/1 (1).jpeg" },
  { name: "Brilliance Cosmocare", image: "/clients/1 (1).jpg"  },
  { name: "Amuchina",             image: "/clients/1 (1).png"  },
  { name: "The Tail Tribe",       image: "/clients/1 (2).png"  },
  { name: "Totaal Beauty",        image: "/clients/1 (3).png"  },
  { name: "Amno Pure",            image: "/clients/1 (4).png"  },
  { name: "Nervfit",              image: "/clients/1 (5).png"  },
  { name: "Soxbox",               image: "/clients/1 (6).png"  },
  { name: "Sun Drunk",            image: "/clients/1 (7).png"  },
  { name: "Tamaira Fashion",      image: "/clients/1 (8).png"  },
  { name: "Kojie San",            image: "/clients/1 (9).png"  },
  { name: "Client",               image: "/clients/1 (1).webp" },
  { name: "Client",               image: "/clients/1 (2).webp" },
];

/* ─── Infinite Marquee ─── */
const ClientMarquee = () => {
  const doubled = [...clientLogos, ...clientLogos];

  return (
    <div style={{ overflow: "hidden", position: "relative" }}>
      {/* Fade edges */}
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: "120px", zIndex: 2,
        background: "linear-gradient(to right, #fff, transparent)",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", right: 0, top: 0, bottom: 0, width: "120px", zIndex: 2,
        background: "linear-gradient(to left, #fff, transparent)",
        pointerEvents: "none",
      }} />

      <div className="marquee-track py-4">
        {doubled.map((client, i) => (
          <div
            key={i}
            style={{
              flexShrink: 0,
              width: "160px",
              height: "80px",
              background: "#fff",
              borderRadius: "12px",
              border: "1px solid #F0F0F0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "12px 20px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
              transition: "box-shadow 0.3s ease",
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.10)"}
            onMouseLeave={e => e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.04)"}
          >
            <img
              src={client.image}
              alt={client.name}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                filter: "grayscale(0%)",
                transition: "filter 0.3s ease",
              }}
              
              
            />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Service Cards Data ─── */
const serviceCards = [
  {
    id: "google-ads",
    icon: Monitor,
    logo: "/platforms/google.png",
    label: "Performance Engine",
    title: "Google Ads Management",
    description:
      "Capture high intent demand with precision built campaigns across Search, Shopping, Display & YouTube designed to convert profitably at scale.",
    color: "#4285F4",
    bgColor: "#EFF6FF",
    tags: ["Search", "Shopping", "Display", "YouTube"],
    link: "/services/google-ads",
  },
  {
    id: "meta-ads",
    icon: Target,
    logo: "/platforms/meta.png",
    label: "Performance Engine",
    title: "Meta Ads",
    description:
      "Facebook & Instagram campaigns engineered to find your ideal customer, deliver thumb stopping creatives, and drive measurable revenue growth.",
    color: "#1877F2",
    bgColor: "#EFF6FF",
    tags: ["Facebook", "Instagram", "Reels", "Stories"],
    link: "/services/meta-ads",
  },
  {
    id: "amazon-ads",
    icon: ShoppingCart,
    logo: "/platforms/amazon_ads.png",
    label: "Marketplace Expansion",
    title: "Amazon Ads & Marketplace",
    description:
      "As an authorized Amazon Ads Partner, we architect full funnel growth systems across Sponsored Products, Brands & Display across 20+ global markets.",
    color: "#FF9900",
    bgColor: "#FFF7ED",
    tags: ["Sponsored Products", "Brands", "DSP", "AMC"],
    link: "/services/amazon-ads",
    badge: "Amazon Partner",
  },
  {
    id: "india-marketplace",
    icon: Globe,
    logo: "/services_ass/Performance_Marketing.png",
    label: "Marketplace Expansion",
    title: "India Marketplace Growth",
    description:
      "Scale profitably across Flipkart, Meesho & Nykaa with catalog optimization, keyword strategy, ads management and conversion focused content.",
    color: "#10B981",
    bgColor: "#ECFDF5",
    tags: ["Flipkart", "Meesho", "Nykaa", "Catalog SEO"],
    link: "/services/marketplace-expansion",
  },
  {
    id: "quick-commerce",
    icon: Zap,
    logo: "/platforms/zepto.png",
    label: "Quick Commerce",
    title: "Quick Commerce Acceleration",
    description:
      "Win the 10 minute delivery economy. We scale brands on Blinkit, Zepto, Swiggy Instamart & Amazon Now with placement, availability & demand strategies.",
    color: "#F59E0B",
    bgColor: "#FFFBEB",
    tags: ["Blinkit", "Zepto", "Swiggy Instamart", "Amazon Now"],
    link: "/services/quick-commerce",
  },
  {
    id: "programmatic",
    icon: Radio,
    logo: "/services_ass/programatic_ads.png",
    label: "Programmatic",
    title: "Programmatic Advertising",
    description:
      "Deploy sophisticated media & measurement platforms to build a robust media mix that lowers incremental cost of customer acquisition at scale.",
    color: "#8B5CF6",
    bgColor: "#F5F3FF",
    tags: ["DSP", "AMC", "Brand Safety", "Attribution"],
    link: "/services/programmatic-advertising",
  },
];

/* ─── Services Section ─── */
const ServicesSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <section className="py-20 md:py-28" style={{ background: "#F8F7FF" }}>
      <div className="max-w-[1320px] mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
          <div>
            <p
              className="text-[13px] font-bold uppercase tracking-[0.2em] mb-3"
              style={{ fontFamily: "'Poppins', sans-serif", color: "#F26522" }}
            >
              What We Do
            </p>
            <h2
              className="text-[36px] md:text-[48px] font-bold leading-tight"
              style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
            >
              Our Services
            </h2>
          </div>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-[#1B1B3A] text-[14px] font-semibold no-underline border-b-2 border-[#1B1B3A] pb-1 hover:text-[#F26522] hover:border-[#F26522] transition-colors self-start md:self-end"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            View all services
            <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {serviceCards.map((service) => {
            const IconComp = service.icon;
            const isHovered = hoveredCard === service.id;

            return (
              <Link
                key={service.id}
                to={service.link}
                className="no-underline"
                onMouseEnter={() => setHoveredCard(service.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div
                  className="relative bg-white rounded-2xl p-7 h-full transition-all duration-300 overflow-hidden flex flex-col"
                  style={{
                    boxShadow: isHovered
                      ? `0 20px 60px rgba(0,0,0,0.12), 0 0 0 2px ${service.color}30`
                      : "0 2px 16px rgba(0,0,0,0.06)",
                    transform: isHovered ? "translateY(-4px)" : "translateY(0)",
                  }}
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl transition-all duration-300"
                    style={{ background: service.color, opacity: isHovered ? 1 : 0 }}
                  />
                  {service.badge && (
                    <div
                      className="absolute top-5 right-5 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                      style={{ background: "#FF9900", color: "#fff", fontFamily: "'Poppins', sans-serif" }}
                    >
                      <Award size={10} />
                      {service.badge}
                    </div>
                  )}
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 flex-shrink-0 transition-all duration-300"
                    style={{ background: service.bgColor }}
                  >
                    {service.logo ? (
                      <img src={service.logo} alt={service.title} className="w-9 h-9 object-contain" />
                    ) : (
                      <IconComp size={24} style={{ color: service.color }} />
                    )}
                  </div>
                  <p
                    className="text-[11px] font-bold uppercase tracking-[0.15em] mb-2"
                    style={{ fontFamily: "'Poppins', sans-serif", color: service.color }}
                  >
                    {service.label}
                  </p>
                  <h3
                    className="text-[18px] font-bold mb-3 leading-tight"
                    style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
                  >
                    {service.title}
                  </h3>
                  <p
                    className="text-[13px] leading-relaxed mb-5 flex-grow"
                    style={{ fontFamily: "'Poppins', sans-serif", color: "#666" }}
                  >
                    {service.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {service.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                        style={{ background: service.bgColor, color: service.color, fontFamily: "'Poppins', sans-serif" }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div
                    className="flex items-center gap-2 text-[13px] font-semibold transition-all duration-300"
                    style={{ fontFamily: "'Poppins', sans-serif", color: isHovered ? service.color : "#1B1B3A" }}
                  >
                    Learn More
                    <ArrowRight
                      size={14}
                      style={{
                        transform: isHovered ? "translateX(4px)" : "translateX(0)",
                        transition: "transform 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ─── Amazon Partner Banner ─── */
const AmazonPartnerBanner = () => (
  <section className="py-12 md:py-16" style={{ background: "#1B1B3A" }}>
    <div className="max-w-[1320px] mx-auto px-6">
      <div
        className="rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-8"
        style={{
          background: "linear-gradient(135deg, #232F3E 0%, #37475A 50%, #232F3E 100%)",
          border: "1px solid rgba(255,153,0,0.3)",
          boxShadow: "0 0 40px rgba(255,153,0,0.1)",
        }}
      >
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ background: "#FFF7ED" }}
          >
            <img src="/platforms/amazon_ads.png" alt="Amazon Ads Partner" className="w-14 h-14 object-contain" />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span
                className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
                style={{ background: "#FF9900", color: "#fff", fontFamily: "'Poppins', sans-serif" }}
              >
                ✓ Authorised Partner
              </span>
              <span
                className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
                style={{ background: "rgba(255,255,255,0.1)", color: "#fff", fontFamily: "'Poppins', sans-serif" }}
              >
                20+ Global Marketplaces
              </span>
            </div>
            <h3
              className="text-[22px] md:text-[26px] font-bold text-white mb-2"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Authorized Amazon Ads Partner
            </h3>
            <p
              className="text-[14px] leading-relaxed max-w-[480px]"
              style={{ fontFamily: "'Poppins', sans-serif", color: "rgba(255,255,255,0.7)" }}
            >
              Trusted by brands across 20+ global marketplaces to manage and scale
              high performance Amazon advertising ecosystems from Sponsored Ads to Amazon DSP.
            </p>
          </div>
        </div>
        <div className="flex gap-8 flex-shrink-0">
          {[
            { value: "20+", label: "Marketplaces" },
            { value: "3×",  label: "Avg. Growth"  },
            { value: "20+", label: "Market in Countries" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-[28px] font-bold" style={{ fontFamily: "'Poppins', sans-serif", color: "#FF9900" }}>
                {stat.value}
              </p>
              <p className="text-[12px] uppercase tracking-wider" style={{ fontFamily: "'Poppins', sans-serif", color: "rgba(255,255,255,0.5)" }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ─── Home Page ─── */
const Home = () => {
  return (
    <div className="overflow-hidden">
      <GlobalStyles />
      <VideoIntro />

      {/* ── Hero Section ── */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="max-w-[1320px] mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="md:w-1/2">
              <h1
                className="text-[32px] md:text-[46px] font-bold leading-tight mb-6"
                style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
              >
                <RotatingHeadline headlines={heroData.rotatingHeadlines} />
              </h1>
              <p
                className="text-[#555] text-[16px] leading-relaxed mb-2 max-w-[500px]"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {heroData.description}
              </p>
              <p
                className="text-[#555] text-[16px] mb-8"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {heroData.subDescription}
              </p>
              <AuditButton to={heroData.ctaLink}>
                {heroData.ctaText}
              </AuditButton>
            </div>
            <div className="md:w-1/2">
              <img
                src={heroData.heroImage}
                alt="Urban Hunter - Digital Marketing"
                className="w-full max-w-[1000px] mx-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── About Section ── */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1320px] mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-12 items-start">
            <div className="md:w-1/3">
              <h2
                className="text-[32px] md:text-[40px] font-bold leading-tight"
                style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
              >
                {aboutSectionData.title}
              </h2>
            </div>
            <div className="md:w-2/3">
              <p
                className="text-[#555] text-[15px] leading-relaxed mb-4"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {aboutSectionData.description}
              </p>
              <p
                className="text-[#555] text-[15px] leading-relaxed mb-6"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {aboutSectionData.subDescription}
              </p>
              <Link
                to={aboutSectionData.linkUrl}
                className="text-[#1B1B3A] text-[13px] font-bold uppercase tracking-wider no-underline border-b-2 border-[#1B1B3A] pb-1 hover:text-[#F26522] hover:border-[#F26522] transition-colors"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {aboutSectionData.linkText}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1320px] mx-auto px-6">
          <h2
            className="text-[32px] md:text-[40px] font-bold text-center mb-8"
            style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
          >
            {whyChooseUsData.title}
          </h2>
          <p
            className="text-[#555] text-[15px] leading-relaxed max-w-[900px] mx-auto text-center mb-4"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {whyChooseUsData.description}
          </p>
          <p
            className="text-[#1B1B3A] text-[16px] font-semibold max-w-[900px] mx-auto mb-14"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {whyChooseUsData.subtitle}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whyChooseUsData.features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div key={index} className="group">
                  <div className="w-16 h-16 rounded-full bg-[#F5F5F5] flex items-center justify-center mb-5 group-hover:bg-[#FFF3EC] transition-colors duration-300">
                    {IconComponent && <IconComponent size={28} className="text-[#F26522]" />}
                  </div>
                  <h3
                    className="text-[16px] font-bold uppercase tracking-wide mb-4"
                    style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className="text-[#555] text-[14px] leading-relaxed"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Services Section ── */}
      <ServicesSection />

      {/* ── Amazon Partner Banner ── */}
      <AmazonPartnerBanner />

      {/* ── Platforms Section ── */}
      <section className="py-16 md:py-24" style={{ background: "#FAFAFA" }}>
        <div className="max-w-[1320px] mx-auto px-6">
          <h2
            className="text-[28px] md:text-[36px] font-bold text-center mb-3"
            style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
          >
            Platforms We Scale On
          </h2>
          <p
            className="text-[#555] text-[15px] text-center mb-12"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            From global marketplaces to India's fastest growing commerce platforms
          </p>
          <PlatformsGrid />
        </div>
      </section>

      {/* ── Our Clients Section ── */}
      <section className="py-16 md:py-24" style={{ background: "#fff" }}>
        <div className="max-w-[1320px] mx-auto px-6">
          <div className="text-center mb-12">
            <p
              className="text-[13px] font-bold uppercase tracking-[0.2em] mb-3"
              style={{ fontFamily: "'Poppins', sans-serif", color: "#F26522" }}
            >
              Trusted By
            </p>
            <h2
              className="text-[28px] md:text-[36px] font-bold"
              style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
            >
              Brands That Scale With Us
            </h2>
            <p
              className="text-[#888] text-[15px] mt-3 max-w-[500px] mx-auto"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              From emerging D2C brands to established market leaders across global marketplaces
            </p>
          </div>
          <ClientMarquee />
        </div>
      </section>

      {/* ── CTA Section ── */}
      <section className="py-20 md:py-28" style={{ background: "#1B1B3A" }}>
        <div className="max-w-[1320px] mx-auto px-6 text-center">
          <p
            className="text-[#F26522] text-[14px] font-semibold uppercase tracking-widest mb-4"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Ready to Scale?
          </p>
          <h2
            className="text-[32px] md:text-[44px] font-bold mb-6 text-white"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            If you're ready to scale with precision,
            <br className="hidden md:block" /> let's talk.
          </h2>
          <p
            className="text-white/70 text-[16px] mb-10 max-w-[560px] mx-auto leading-relaxed"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Request a private growth audit and speak with a strategy expert to find out exactly
            how Urban Hunter can grow your brand.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <AuditButton to="/free-audit">
              Request a Free Growth Audit
            </AuditButton>

            <Link
              to="/contact-us"
              className="inline-flex items-center justify-center gap-2 bg-white/10 text-white border border-white/20 px-8 py-4 rounded-full text-[14px] font-semibold tracking-wider uppercase no-underline hover:bg-white/20 transition-colors duration-300"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Speak With a Strategy Expert
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
